/**
 * AR Exception Report - Invoice Table Component
 *
 * Detailed invoice-level view with conditional formatting
 */

import { useMemo } from 'react';
import type { MultiValue } from 'react-select';

import { Select, type SelectOption, Icon, showToast } from '@repo/ui';
import { DataTable, type DataTableColumn } from '@xtnd/web/shared/components/DataTable';
import {
  useArAvailableClients,
  useArExceptions,
  useArExport,
} from '../../../queries/arReportQuery';
import { AR_STATUS_OPTIONS } from '../../../constants';
import type { ArFiltersState, ArExceptionFlag } from '../../../queries/arReportQuery';
import { numberUtil } from '@xtnd/web/shared/utils';
import { useReportFilters } from '../../../hooks';
import { ExportButton, type ExportFormat } from '../../../components/ExportButton';

const FLAG_COLORS: Record<any, { bg: string; text: string; border: string }> = {
  OK: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
  VARIANCE_NON_ZERO: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
  MISSING_IN_QBO: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  MISSING_IN_CLIO: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  DUPLICATE: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
};

const FLAG_LABELS: Record<any, string> = {
  OK: 'No Issues',
  VARIANCE_NON_ZERO: 'Variance ≠ 0',
  MISSING_IN_QBO: 'Missing in QBO',
  MISSING_IN_CLIO: 'Missing in Clio',
  DUPLICATE: 'Duplicate',
};

export function ArInvoiceTable() {
  const { filters, pagination, updateFilters, updatePagination } =
    useReportFilters<ArFiltersState>();

  // Fetch data
  const { data, isLoading, error, refetch } = useArExceptions(
    { ...filters, page: pagination.page, limit: pagination.limit },
    { enabled: true }
  );

  // Export mutation
  const exportMutation = useArExport();

  const handleExport = async (format: ExportFormat) => {
    try {
      const exportType = format === 'excel' ? 'xls' : format;
      await exportMutation.mutateAsync({
        type: exportType as 'csv' | 'xls' | 'pdf',
        scope: 'invoices',
        filters: filters || {},
      });
      showToast.success(`Export as ${format.toUpperCase()} started successfully`);
    } catch (error) {
      showToast.error(
        `Failed to export: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  };

  // Get available clients for filters
  const { data: clientsData, isLoading: clientsLoading } = useArAvailableClients();

  const availableClients = useMemo(() => {
    if (!clientsData) return [];
    return clientsData.map((client: any) => ({
      id: client.clientId || client.id,
      name: client.clientName || client.name,
    }));
  }, [clientsData]);

  // Convert available clients to options format
  const clientOptions: SelectOption[] = useMemo(() => {
    return availableClients.map(client => ({
      value: client.id,
      label: client.name,
    }));
  }, [availableClients]);

  // Get selected client options
  const selectedClientOptions: SelectOption[] = useMemo(() => {
    if (!filters || !filters?.clients) return [];
    return availableClients
      .filter(client => filters.clients.includes(client.id))
      .map(client => ({
        value: client.id,
        label: client.name,
      }));
  }, [availableClients, filters]);

  // Get selected status options
  const selectedStatusOptions: SelectOption[] = useMemo(() => {
    if (!filters || !filters?.flags) return [];
    return filters.flags.map(flag => {
      const matchingOption = AR_STATUS_OPTIONS.find(
        (option: SelectOption) => option.value === flag
      );
      return (
        (matchingOption as SelectOption) || {
          value: flag,
          label: flag,
        }
      );
    });
  }, [filters]);

  // Handle client selection change
  const handleClientChange = (newValue: MultiValue<SelectOption> | null) => {
    const clients = newValue?.map(option => option.value) || [];
    const newFilters: ArFiltersState = {
      ...filters,
      clients,
    };
    updateFilters(newFilters, true); // Reset page to 1 when filters change
  };

  // Handle status selection change
  const handleStatusChange = (newValue: MultiValue<SelectOption> | null) => {
    const selectedFlags = (newValue?.map((option: SelectOption) => option.value) ||
      []) as ArExceptionFlag[];
    const newFilters: ArFiltersState = {
      ...filters,
      flags: selectedFlags,
    };
    updateFilters(newFilters, true); // Reset page to 1 when filters change
  };

  const getVarianceColor = (variance: number) => {
    if (Math.abs(variance) < 0.01) return 'text-green-600';
    return variance > 0 ? 'text-red-600' : 'text-blue-600';
  };

  // Define columns for DataTable
  const columns: DataTableColumn<any>[] = useMemo(
    () => [
      {
        key: 'client',
        header: 'Client',
        align: 'left',
        renderCell: (invoice: any) => (
          <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
            <div className="space-y-1">
              {invoice?.clio?.clientName && (
                <div className="text-xs text-blue-600 dark:text-blue-400">
                  Clio: {invoice.clio.clientName}
                </div>
              )}
              {invoice?.qbo?.clientName && (
                <div className="text-xs text-green-600 dark:text-green-400">
                  QBO: {invoice.qbo.clientName}
                </div>
              )}
            </div>
          </div>
        ),
      },
      {
        key: 'billNumber',
        header: 'Bill Number',
        align: 'left',
        renderCell: (invoice: any) => (
          <div className="space-y-1">
            {invoice?.clio?.invoiceNumber && (
              <div className="flex items-center space-x-1">
                <span className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-400">Clio:</span>
                <a
                  href={`https://app.clio.com/bills/${invoice.clio.id || ''}`}
                  target="clio-tab"
                  rel="noopener noreferrer"
                  className="text-sm text-xtnd-blue hover:text-xtnd-blue-600 dark:text-xtnd-blue-400 dark:hover:text-xtnd-blue-300 flex items-center space-x-1 hover:underline"
                >
                  <span>{invoice.clio.invoiceNumber}</span>
                  <Icon name="HiExternalLink" className="h-3 w-3 flex-shrink-0" />
                </a>
              </div>
            )}
            {invoice?.qbo?.invoiceNumber && (
              <div className="flex items-center space-x-1">
                <span className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-400">QBO:</span>
                <a
                  href={`https://qbo.intuit.com/app/invoice?txnId=${invoice.qbo.id || ''}`}
                  target="qbo-tab"
                  rel="noopener noreferrer"
                  className="text-sm text-xtnd-blue hover:text-xtnd-blue-600 dark:text-xtnd-blue-400 dark:hover:text-xtnd-blue-300 flex items-center space-x-1 hover:underline"
                >
                  <span>{invoice.qbo.invoiceNumber}</span>
                  <Icon name="HiExternalLink" className="h-3 w-3 flex-shrink-0" />
                </a>
              </div>
            )}
          </div>
        ),
      },
      {
        key: 'clioOpen',
        header: (
          <div className="flex items-center justify-end gap-1">
            <Icon name="HiDatabase" className="h-3 w-3" />
            Clio Open
          </div>
        ),
        align: 'right',
        headerClassName: 'bg-blue-50 dark:bg-blue-900/30',
        className: 'text-blue-700 dark:text-blue-200',
        renderCell: (invoice: any) => (
          <span className="text-sm text-xtnd-dark-800 dark:text-xtnd-dark-50">
            {invoice?.clio?.open ? numberUtil.formatCurrency(invoice.clio.open) : '-'}
          </span>
        ),
      },
      {
        key: 'qboOpen',
        header: (
          <div className="flex items-center justify-end gap-1">
            <Icon name="HiCash" className="h-3 w-3" />
            QBO Open
          </div>
        ),
        align: 'right',
        headerClassName: 'bg-green-50 dark:bg-green-900/30',
        className: 'text-green-700 dark:text-green-200',
        renderCell: (invoice: any) => (
          <span className="text-sm text-xtnd-dark-800 dark:text-xtnd-dark-50">
            {invoice?.qbo?.open ? numberUtil.formatCurrency(invoice.qbo.open) : '-'}
          </span>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        align: 'center',
        renderCell: (invoice: any) => (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${FLAG_COLORS[invoice.flag]?.bg || 'bg-green-50'} ${FLAG_COLORS[invoice.flag]?.text || 'text-green-700'} ${FLAG_COLORS[invoice.flag]?.border || 'border-green-200'}`}
          >
            {FLAG_LABELS[invoice.flag] || 'Unknown'}
          </span>
        ),
      },
    ],
    []
  );

  const renderExpandedRow = (invoice: any) => (
    <div className="space-y-3">
      {/* Payment Details */}
      {invoice.paymentVariance !== undefined && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <span className="font-medium text-xtnd-dark dark:text-xtnd-light">Clio Total:</span>
            <span className="ml-2 text-xtnd-dark dark:text-xtnd-light">
              {invoice?.clio?.total ? numberUtil.formatCurrency(invoice.clio.total) : '-'}
            </span>
            <div className="text-xs text-xtnd-dark dark:text-xtnd-light">
              Paid: {invoice?.clio?.paid ? numberUtil.formatCurrency(invoice.clio.paid) : '-'}
            </div>
          </div>
          <div>
            <span className="font-medium text-xtnd-dark dark:text-xtnd-light">QBO Total:</span>
            <span className="ml-2 text-xtnd-dark dark:text-xtnd-light">
              {invoice?.qbo?.total ? numberUtil.formatCurrency(invoice.qbo.total) : '-'}
            </span>
            <div className="text-xs text-xtnd-dark dark:text-xtnd-light">
              Paid: {invoice?.qbo?.paid ? numberUtil.formatCurrency(invoice.qbo.paid) : '-'}
            </div>
          </div>
          <div>
            <span className="font-medium text-xtnd-dark dark:text-xtnd-light">
              Payment Variance:
            </span>
            <span className={`ml-2 ${getVarianceColor(invoice.paymentVariance || 0)}`}>
              {numberUtil.formatCurrency(invoice.paymentVariance || 0)}
            </span>
          </div>
        </div>
      )}

      {/* Notes */}
      {invoice.notes && (
        <div className="bg-white dark:bg-xtnd-dark-700 rounded-lg border border-xtnd-dark-100 dark:border-xtnd-dark-600 p-4">
          <span className="font-medium text-blue-900 dark:text-xtnd-light">Notes:</span>
          <p className="text-blue-800 dark:text-xtnd-light text-sm mt-1">{invoice.notes}</p>
        </div>
      )}
    </div>
  );

  const isRowExpandable = (invoice: any) => {
    return !!(invoice.notes || invoice.paymentVariance !== undefined);
  };

  const renderHeader = () => (
    <>
      {/* Filters Section */}
      <div className="border-b border-xtnd-light dark:border-xtnd-dark-700 pb-4 mb-6">
        <div className="flex flex-wrap items-start gap-2 md:items-center md:flex-nowrap">
          {/* Client Filter */}
          <div className="flex items-center gap-2 w-full md:basis-[45%] md:w-auto">
            <span className="text-sm text-xtnd-dark-600 dark:text-xtnd-dark-400 whitespace-nowrap">
              Client:
            </span>
            <div className="flex-1">
              <Select<SelectOption>
                value={selectedClientOptions}
                onChange={handleClientChange}
                options={clientOptions}
                placeholder="Filter by client..."
                disabled={clientsLoading || isLoading}
                isLoading={clientsLoading || isLoading}
                multiple={true}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                clearable={true}
                searchable={true}
                menuPlacement="auto"
                menuPosition="fixed"
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              />
            </div>
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-2 w-full md:basis-[45%] md:w-auto">
            <span className="text-sm text-xtnd-dark-600 dark:text-xtnd-dark-400 whitespace-nowrap">
              Status:
            </span>
            <div className="flex-1">
              <Select<SelectOption>
                value={selectedStatusOptions}
                onChange={handleStatusChange}
                options={AR_STATUS_OPTIONS}
                placeholder="Filter by status..."
                multiple={true}
                isLoading={isLoading}
                disabled={isLoading}
                classNamePrefix="react-select"
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                clearable={true}
                searchable={false}
                menuPlacement="auto"
                menuPosition="fixed"
                showOptionBullets={true}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              />
            </div>
          </div>

          {/* Selected counters */}
          <div className="w-full md:basis-[10%] md:w-auto">
            <div className="flex flex-col items-start md:items-end gap-1 mt-2 md:mt-0">
              {filters?.clients && filters.clients.length > 0 && (
                <span className="text-xs text-xtnd-dark-500 dark:text-xtnd-light whitespace-nowrap">
                  {filters.clients.length} client{filters.clients.length > 1 ? 's' : ''} selected
                </span>
              )}
              {filters?.flags && filters.flags.length > 0 && (
                <span className="text-xs text-xtnd-dark-500 dark:text-xtnd-light whitespace-nowrap">
                  {filters.flags.length} status{filters.flags.length > 1 ? 'es' : ''} selected
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <DataTable
      columns={columns}
      data={data}
      isLoading={isLoading}
      error={error}
      onRetry={() => refetch()}
      pagination={pagination}
      onPaginationChange={updatePagination}
      renderHeader={renderHeader}
      renderAction={() => (
        <ExportButton
          onExport={handleExport}
          disabled={isLoading}
          loading={exportMutation.isPending}
        />
      )}
      renderExpandedRow={renderExpandedRow}
      isRowExpandable={isRowExpandable}
      emptyState={{
        icon: 'HiClipboardList',
        title: 'No invoice data available',
        description: 'There are no invoices matching your current filters.',
      }}
      tableContainerClassName="overflow-y-auto relative"
      tableClassName="min-w-full divide-y divide-xtnd-dark-100 relative"
      stickyHeader={true}
      tableMaxHeight="calc(100vh-300px)"
      pageSizeOptions={[25, 50, 100, 200]}
      loadingRowCount={10}
      loadingColumnCount={6}
      getRowKey={(invoice: any, index: number) => `${invoice.billId || 'no-id'}-${index}`}
    />
  );
}
